import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Wrench } from "lucide-react";
import { COMPANY } from "@/lib/siteData";

export default function Footer() {
  return (
    <footer className="bg-[#001B35] text-white dark-grain pb-20 md:pb-0" data-testid="site-footer">
      <div className="border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid md:grid-cols-2 gap-6 items-center">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#005BBB]/20 flex items-center justify-center">
              <Wrench className="w-7 h-7 text-[#9bb6d6]" />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#9bb6d6]">Un projet ? Un dépannage ?</p>
              <p className="font-display text-2xl sm:text-3xl font-bold">Parlons-en !</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <a href={`tel:${COMPANY.phoneTel}`} data-testid="footer-cta-phone" className="inline-flex items-center gap-2 bg-[#005BBB] hover:bg-[#0072e8] rounded-full px-5 py-3 font-semibold transition btn-shine">
              <Phone className="w-4 h-4" /> {COMPANY.phone}
            </a>
            <Link to="/contact" data-testid="footer-cta-devis" className="inline-flex items-center gap-2 bg-[#D90000] hover:bg-[#b00000] rounded-full px-5 py-3 font-semibold transition btn-shine">
              Devis gratuit
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-5 md:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo-rm.jpg" alt="RM Plomberie" className="h-12 w-12 rounded-lg object-cover" />
            <div>
              <p className="font-display font-bold">{COMPANY.shortName}</p>
              <p className="text-[10px] uppercase tracking-[0.18em] text-[#9bb6d6]">Chauffage • VMC</p>
            </div>
          </div>
          <p className="text-sm text-[#9bb6d6] leading-relaxed">
            Artisan plombier chauffagiste à {COMPANY.city}, intervention dans tout le Haut-Rhin (68) et Bas-Rhin.
          </p>
          <p className="text-xs text-[#9bb6d6]/70 mt-4">
            SIREN {COMPANY.siren} • SIRET {COMPANY.siret}
          </p>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Liens utiles</h4>
          <ul className="space-y-2 text-sm text-[#9bb6d6]">
            <li><Link to="/" className="hover:text-white">Accueil</Link></li>
            <li><Link to="/nos-services" className="hover:text-white">Nos services</Link></li>
            <li><Link to="/realisations" className="hover:text-white">Réalisations</Link></li>
            <li><Link to="/a-propos" className="hover:text-white">À propos</Link></li>
            <li><Link to="/google-business" className="hover:text-white">Google Business</Link></li>
            <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Villes desservies</h4>
          <ul className="space-y-2 text-sm text-[#9bb6d6]">
            <li><Link to="/plombier-colmar" className="hover:text-white">Plombier à Colmar</Link></li>
            <li><Link to="/plombier-beblenheim" className="hover:text-white">Plombier à Beblenheim</Link></li>
            <li><Link to="/plombier-ribeauville" className="hover:text-white">Plombier à Ribeauvillé</Link></li>
            <li><Link to="/plombier-kaysersberg" className="hover:text-white">Plombier à Kaysersberg</Link></li>
            <li><Link to="/plombier-selestat" className="hover:text-white">Plombier à Sélestat</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Services à Colmar</h4>
          <ul className="space-y-2 text-sm text-[#9bb6d6]">
            <li><Link to="/depannage-plomberie-colmar" className="hover:text-white">Dépannage plomberie</Link></li>
            <li><Link to="/urgence-plombier-colmar" className="hover:text-white">Urgence plombier</Link></li>
            <li><Link to="/recherche-fuite-colmar" className="hover:text-white">Recherche de fuite</Link></li>
            <li><Link to="/debouchage-canalisation-colmar" className="hover:text-white">Débouchage canalisation</Link></li>
            <li><Link to="/chauffagiste-colmar" className="hover:text-white">Chauffagiste</Link></li>
            <li><Link to="/entretien-chaudiere-colmar" className="hover:text-white">Entretien chaudière</Link></li>
            <li><Link to="/installation-vmc-colmar" className="hover:text-white">Installation VMC</Link></li>
            <li><Link to="/renovation-salle-de-bain-colmar" className="hover:text-white">Rénovation salle de bain</Link></li>
          </ul>
        </div>

        <div>
          <h4 className="font-display font-bold mb-4 text-sm uppercase tracking-wider">Contact</h4>
          <ul className="space-y-3 text-sm text-[#9bb6d6]">
            <li className="flex items-start gap-3">
              <Phone className="w-4 h-4 mt-0.5 text-[#005BBB]" />
              <a href={`tel:${COMPANY.phoneTel}`} className="hover:text-white">{COMPANY.phone}</a>
            </li>
            <li className="flex items-start gap-3">
              <Mail className="w-4 h-4 mt-0.5 text-[#005BBB]" />
              <a href={`mailto:${COMPANY.email}`} className="hover:text-white break-all">{COMPANY.email}</a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin className="w-4 h-4 mt-0.5 text-[#005BBB]" />
              <span>{COMPANY.address}</span>
            </li>
          </ul>
          <p className="mt-4 text-xs uppercase tracking-wider text-[#9bb6d6]/70">Zone : Beblenheim • Colmar • Mulhouse • Strasbourg</p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row gap-3 justify-between items-center text-xs text-[#9bb6d6]">
          <p>© 2026 RM Plomberie Chauffage VMC — Tous droits réservés</p>
          <div className="flex gap-5">
            <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
            <Link to="/confidentialite" className="hover:text-white">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
